import { useEffect, useRef } from "react";
import { get } from "lodash";
import { UnaryFn } from "@tvg/ts-types/Functional";
import { RaceInfoMyBets, RaceProgram } from "@tvg/ts-types/Race";
import { WroWagerGroup } from "@tvg/ts-types/WroWager";
import { getRacesIds } from "../utils/raceDetails";
import { SettledTabEnum } from "../utils/types";

let unsubscribe: Function | void | null;

interface prevInterface {
  raceDate: string;
  races: Array<RaceInfoMyBets | RaceProgram>;
}

interface subscriptionInterface {
  subscriptionData: {
    data: {
      racesByTrackCodes: Array<RaceInfoMyBets | RaceProgram>;
    };
  };
}

export const updateRacesQueryFunction = (
  prev: prevInterface,
  { subscriptionData }: subscriptionInterface
) => {
  if (!get(subscriptionData, "data.racesByTrackCodes.length")) {
    return prev;
  }

  const prevRaces = get(prev, "races", []);
  const updatedRaces = get(subscriptionData, "data.racesByTrackCodes", []);

  const newRaces = prevRaces.map((race: RaceInfoMyBets | RaceProgram) => {
    const { id: currentRaceId } = race;
    const updatedRace = updatedRaces.find(
      (ur: RaceInfoMyBets | RaceProgram) => ur.id === currentRaceId
    );
    return updatedRace ? { ...race, ...updatedRace } : race;
  });

  return {
    raceDate: prev.raceDate,
    races: newRaces
  };
};

function useGraphRacesSubscription(
  RacesSubscription: unknown,
  isLogged: boolean,
  subscribeToMore: UnaryFn<unknown, void>,
  selectedSettledTab: SettledTabEnum,
  wagerProfile: string,
  bets: WroWagerGroup[]
) {
  const betsRef = useRef(bets);
  betsRef.current = bets;

  const lastUpdateTimeRef = useRef(0);

  // Derive a lightweight key from race IDs instead of serializing the entire
  // bets payload (~645KB). This re-triggers the subscription only when the
  // set of tracked races actually changes.
  const raceIdsKey = getRacesIds(bets).join(",");

  useEffect(() => {
    const races = getRacesIds(betsRef.current);
    const isSettledTodayBetsTab = selectedSettledTab === SettledTabEnum.TODAY;
    if (
      isLogged &&
      betsRef.current.length &&
      typeof subscribeToMore === "function" &&
      isSettledTodayBetsTab
    ) {
      // Throttle subscription processing to at most once per second
      const throttledUpdateQuery = (
        prev: prevInterface,
        data: subscriptionInterface
      ) => {
        const now = Date.now();
        if (now - lastUpdateTimeRef.current < 1000) {
          return prev;
        }
        lastUpdateTimeRef.current = now;
        return updateRacesQueryFunction(prev, data);
      };

      unsubscribe = subscribeToMore({
        document: RacesSubscription,
        variables: { wagerProfile, races },
        updateQuery: throttledUpdateQuery
      });
    } else if (typeof unsubscribe === "function") {
      unsubscribe();
      unsubscribe = null;
    }
    // On component will unmount, unsubscribes to websocket
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
        unsubscribe = null;
      }
    };
  }, [subscribeToMore, isLogged, selectedSettledTab, raceIdsKey]);

  return null;
}

export default useGraphRacesSubscription;
